/**
 * Reads the server-populated referrer value that mirrors the HTTP `Referer` header
 * captured during document rendering.
 *
 * @returns The server-populated referrer when it is a non-empty string; otherwise `''`.
 */
function getServerSideReferrer(): string {
    const { httpReferrer } = window as Window & { httpReferrer?: unknown };
    return typeof httpReferrer === 'string' && httpReferrer ? httpReferrer : '';
}

/**
 * Reads the browser-managed referrer for the current document from `document.referrer`.
 *
 * @returns The browser-managed document referrer when present; otherwise `''`.
 */
function getDocumentReferrerClientSideOnly(): string {
    return window.document.referrer;
}

/**
 * Resolves the current document referrer with server-side priority for first-load flows.
 *
 * This helper exists because browser-managed `document.referrer` can be empty in
 * some navigation scenarios (for example iOS new-tab handoff flows), while the server
 * can still capture the incoming HTTP `Referer` header and expose it as
 * `window.httpReferrer`.
 *
 * Priority order:
 * 1. `window.httpReferrer`
 * 2. `window.document.referrer`
 * 3. `''`
 *
 * This server-side-first order is intended for first-load authentication bootstrap
 * flows where the browser-managed referrer can be empty even though the server
 * captured the incoming HTTP `Referer` header during document rendering.
 *
 * @returns A non-null string containing the best available referrer value.
 */
export function getDocumentRefererServerSideFirst(): string {
    return getServerSideReferrer() || getDocumentReferrerClientSideOnly();
}

/**
 * Resolves the current document referrer with client-side priority for browser-native flows.
 *
 * This helper exists for flows that should prefer the browser-managed
 * `document.referrer` semantics first, while still allowing the server-populated
 * `window.httpReferrer` value to fill gaps when the browser-managed referrer is empty.
 *
 * Priority order:
 * 1. `window.document.referrer`
 * 2. `window.httpReferrer`
 * 3. `''`
 *
 * @returns A non-null string containing the best available referrer value.
 */
export function getDocumentRefererClientSideFirst(): string {
    return getDocumentReferrerClientSideOnly() || getServerSideReferrer();
}
